// PlayerProfilePdf.js
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { addPdfDisclaimer } from '../utils/pdfDisclaimer';

/**
 * Convertit un code HTML en texte brut.
 * @param {string} html 
 * @returns {string}
 */
function convertHtmlToText(html) {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function traverse(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      let text = '';

      if (tag === 'br') {
        return '\n';
      }
      if (tag === 'p' || tag === 'div') {
        node.childNodes.forEach(child => {
          text += traverse(child);
        });
        text += ' ';
        return text;
      }
      if (tag === 'ol') {
        let index = 1;
        node.childNodes.forEach(child => {
          if (child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'li') {
            text += index + '. ' + traverse(child) + '\n';
            index++;
          } else {
            text += traverse(child);
          }
        });
        return text;
      }
      if (tag === 'ul') {
        node.childNodes.forEach(child => {
          if (child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'li') {
            text += '• ' + traverse(child) + '\n';
          } else {
            text += traverse(child);
          }
        });
        return text;
      }
      if (tag === 'li') {
        text += Array.from(node.childNodes).map(child => traverse(child)).join('');
        return text;
      }
      node.childNodes.forEach(child => {
        text += traverse(child);
      });
      return text;
    }
    return '';
  }

  let result = traverse(doc.body);
  // Nettoyage des espaces / retours à la ligne superflus
  result = result.replace(/\n{2,}/g, "\n").trim();
  return result;
}

/**
 * Formate la date au format jj/mm/aaaa pour l'affichage.
 * @param {string} dateString - date au format ISO, ex: "2023-04-12"
 * @returns {string} ex: "12/04/2023"
 */
function formatDateForDisplay(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Selon la mention "video" ou "live", renvoie une chaîne formatée
 * @param {string} visu 
 * @returns {string}
 */
function formatScoutingType(visu) {
  if (!visu) return '';
  const lower = visu.toLowerCase();
  if (lower.includes('video')) return 'Video';
  if (lower.includes('live')) return 'Live';
  return visu;
}

/**
 * Ajoute conditionnellement une ligne dans un tableau si la valeur n'est pas vide.
 * @param {Array} arr 
 * @param {string} label 
 * @param {string|number} value 
 * @param {string} suffix 
 */
function pushNonEmptyRow(arr, label, value, suffix = '') {
  if (value && value.toString().trim() !== '') {
    arr.push([label, suffix ? value + suffix : value]);
  }
}

/**
 * Dessine un bloc de texte multi-lignes en gérant le saut de page si nécessaire.
 */
function drawTextBlock(
  doc,
  text,
  yPos,
  marginLeft,
  contentWidth,
  pageHeight,
  bottomMargin,
  fontFamily = 'helvetica',
  fontStyle = 'normal',
  fontSize = 11
) {
  if (doc.getFont() !== fontFamily || doc.getFontType() !== fontStyle) {
    doc.setFont(fontFamily, fontStyle);
  }
  if (doc.getFontSize() !== fontSize) {
    doc.setFontSize(fontSize);
  }
  const lines = doc.splitTextToSize(text, contentWidth);
  for (let i = 0; i < lines.length; i++) {
    if (yPos + fontSize > pageHeight - bottomMargin) {
      doc.addPage();
      addPageTemplate(doc);
      doc.setFont(fontFamily, fontStyle);
      doc.setFontSize(fontSize);
      yPos = 20;
    }
    doc.text(lines[i], marginLeft, yPos);
    yPos += 6;
  }
  return yPos;
}

/**
 * Ajoute le "template" pour chaque page PDF (bordure, bas de page, logos, etc.)
 * @param {jsPDF} doc
 * @param {Object} clubInfo - Infos du club (name, primaryColor)
 * @param {Object} translations - Traductions pour le PDF
 */
function addPageTemplate(doc, clubInfo = {}, translations = {}) {
  const pageNumber = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Couleur du club ou couleur par défaut
  const primaryColor = clubInfo.primaryColor || '#EA7F00';
  const clubName = clubInfo.name || 'CLUB';
  const departmentText = translations.footer?.department || 'DEPARTAMENTO DE SCOUTING';

  // Convertir couleur hex en RGB pour setFillColor
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 234, g: 127, b: 0 };
  };
  const rgb = hexToRgb(primaryColor);

  // Bords
  doc.setDrawColor(primaryColor);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

  // Bandeau en bas
  const bandHeight = 20;
  const bandY = pageHeight - 10 - bandHeight;
  doc.setFillColor(rgb.r, rgb.g, rgb.b);
  doc.rect(10, bandY, pageWidth - 20, bandHeight, 'F');

  // Logos et texte
  const logoSize = 16;
  try {
    const logoY = bandY + (bandHeight - logoSize) / 2;

    // Déterminer les logos selon l'URL de l'application
    const currentUrl = window.location.href.toLowerCase();
    const isKansas = currentUrl.includes('kansas');
    const isMetropolitans = currentUrl.includes('metropolitans');
    const isByers = currentUrl.includes('byers');
    const logoLeft = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/LogoValenciaBasket.png`;
    const logoRight = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/alqueria-del-basket.png`;

    doc.addImage(
      logoLeft,
      'PNG',
      15,
      logoY,
      logoSize,
      logoSize
    );
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(
      `${clubName} - ${departmentText}`,
      pageWidth / 2,
      bandY + bandHeight / 2 + 3,
      { align: 'center' }
    );
    doc.addImage(
      logoRight,
      'PNG',
      pageWidth - 15 - logoSize,
      logoY,
      logoSize,
      logoSize
    );
    doc.setFontSize(8);
    doc.text(pageNumber.toString(), pageWidth / 2, bandY + bandHeight - 3, { align: 'center' });
  } catch (error) {
    console.error('Error adding images to PDF page:', error);
  }
  doc.setTextColor(0);
  addPdfDisclaimer(doc);
}

/**
 * Fonction principale : Génère et télécharge le PDF selon les sélections de l'utilisateur
 */
export async function generatePDF({
  player,
  selectedImagePreview,
  avatarTimestamp,
  mediaUrl,
  backendUrl,
  section,
  includeProfileInfo,
  includeCaracteristicas,
  includeExperiencia,
  includeContrato,
  checkedInformes,
  checkedIntels,
  clubInfo = {},
  translations = {},
  translateValue = (val) => val // Fonction par défaut qui retourne la valeur telle quelle
}) {
  // Labels par défaut (espagnol) si pas de traductions fournies
  const labels = translations.labels || {
    weight: 'Peso', height: 'Altura', birthDate: 'Fecha de Nacimiento',
    nationality: 'Nacionalidad', secondNationality: '2ª Nacionalidad',
    geneticOrigin: 'Origen Genético', nationalTeam: 'Selección', team: 'Equipo',
    secondaryLeague: 'Liga Secundaria', groups: 'Grupo(s)',
    position: 'Posición', dominantHand: 'Mano Dominante', shootingRange: 'Shooting Range',
    offensiveRole: 'Rol Ofensivo', defensiveRole: 'Rol Defensivo', jeJiSkills: 'Skills JE/JI',
    attributes: 'Atributos', flagBehaviour: 'Flag Behaviour', injuryProne: 'Injury Prone',
    currentLevel: 'Nivel (actual)', projection: 'Proyección', proComparison: 'Comparación Pro',
    agency: 'Agencia', salaryRange: 'Rango Salarial', contract: 'Contrato', buyout: 'Buyout',
    draftYear: 'Año Draft', draftPosition: 'Posición Draft', preDraftTeam: 'Pre-Draft Team',
    pit: 'PIT', eurocamp: 'Eurocamp', nbaExperience: 'Experiencia NBA',
    acbExperience: 'Experiencia ACB', europeExperience: 'Experiencia Europa',
    ncaaExperience: 'Experiencia NCAA', scouting: 'Scouting', event: 'Evento', source: 'Fuente',
    ranking: 'Ranking', tracking: 'Seguimiento'
  };
  const sections = translations.sections || {
    characteristics: 'Características', professionalExperience: 'Experiencia Profesional',
    contractInfo: 'Información Contractual', report: 'Informe', intel: 'INTEL'
  };
  const filenamePrefix = translations.filename?.prefix || 'Informe';

  // Helper pour traduire les valeurs des champs (gère clés de traduction ET valeurs déjà traduites)
  const tv = (value, fieldType) => translateValue(value, fieldType);

  let imgData = null;

  // 1) Tenter de récupérer l'image (locale si modifiée, sinon via proxy)
  try {
    if (selectedImagePreview) {
      // Image modifiée (sélectionnée par l'utilisateur)
      const imgResponse = await fetch(selectedImagePreview);
      if (imgResponse.ok) {
        const blob = await imgResponse.blob();
        imgData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
    } else {
      // Image depuis le serveur
      const avatarUrl = `${mediaUrl}/photos/${player.id}.png?${avatarTimestamp}`;
      // Proxy pour contourner les CORS
      const response = await fetch(
        `${backendUrl}/api/proxy-image?url=${encodeURIComponent(avatarUrl)}`
      );
      if (response.ok) {
        const blob = await response.blob();
        imgData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la photo:', error);
  }

  // 2) Création du doc
  const doc = new jsPDF();
  addPageTemplate(doc, clubInfo, translations);

  // Configuration d'autoTable si nécessaire
  doc.autoTableSetDefaults({
    didDrawPage: function () {
      // Pour redessiner le template si autoTable déclenche un saut de page
      addPageTemplate(doc, clubInfo, translations);
    }
  });

  const bottomMargin = 30;
  const marginLeft = 20;
  const marginRight = 20;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const contentWidth = pageWidth - marginLeft - marginRight;

  let yPos = 20;

  // Fonction utilitaire : ajoute une nouvelle page si l'espace n'est pas suffisant
  const addNewPageIfNeeded = (neededSpace = 20) => {
    if (yPos + neededSpace > pageHeight - bottomMargin) {
      doc.addPage();
      addPageTemplate(doc, clubInfo, translations);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      yPos = 20;
    }
  };

  // Mesure la hauteur (mm) d'un bloc Informe/Intel : titre + lignes d'entête + corps + marge basse.
  const measureBlockHeight = (titleSpace, headerLineCount, bodyText, trailingSpace = 4) => {
    let h = titleSpace + headerLineCount * 6;
    if (bodyText) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(bodyText, contentWidth);
      h += lines.length * 6;
    }
    return h + trailingSpace;
  };

  // Force un saut de page si le bloc ne rentre pas, sauf s'il est plus grand qu'une page entière
  // (dans ce cas on le laisse couler — pagination interne via drawTextBlock).
  const ensureBlockFits = (blockHeight) => {
    const remaining = pageHeight - bottomMargin - yPos;
    const fullPage = pageHeight - bottomMargin - 20;
    if (blockHeight > remaining && blockHeight <= fullPage) {
      doc.addPage();
      addPageTemplate(doc, clubInfo, translations);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      yPos = 20;
    }
  };

  // 3) En-tête avec photo + Nom
  const photoSize = 35;
  if (imgData) {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(marginLeft, yPos, photoSize + 4, photoSize + 4);
    doc.addImage(imgData, 'PNG', marginLeft + 2, yPos + 2, photoSize, photoSize);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(0);
  const nameX = marginLeft + photoSize + 10;
  const nameYPos = yPos + photoSize / 2 + 5;
  doc.text(player.nom || '', nameX, nameYPos, { align: 'left' });
  yPos += photoSize + 10;

  // (Exemple) Si le joueur a des tags (ranking, seguimiento), on peut les afficher
  if (player.tags && player.tags.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    const tagsLine = player.tags
      .map((tag) =>
        tag.source === 'big_board'
          ? `${labels.ranking}: ${tag.libelle}`
          : `${labels.tracking}: ${tag.libelle}`
      )
      .join(' | ');
    const splitTags = doc.splitTextToSize(tagsLine, contentWidth);
    addNewPageIfNeeded(10 * splitTags.length);
    doc.text(splitTags, marginLeft, yPos);
    yPos += 8 * splitTags.length;
  }

  // 4) Inclusion conditionnelle des sections du profil
  if (includeProfileInfo) {
    const profileRows = [];
    pushNonEmptyRow(profileRows, labels.weight, player.peso, ' kg');
    pushNonEmptyRow(profileRows, labels.height, player.taille, ' cm');
    if (player.dateNaissance) {
      pushNonEmptyRow(profileRows, labels.birthDate, formatDateForDisplay(player.dateNaissance));
    }
    pushNonEmptyRow(profileRows, labels.nationality, tv(player.nacionalidad, 'nationality'));
    pushNonEmptyRow(profileRows, labels.secondNationality, tv(player.segundaNacionalidad, 'nationality'));
    pushNonEmptyRow(profileRows, labels.geneticOrigin, tv(player.origen_genetico, 'geneticOrigin'));
    pushNonEmptyRow(profileRows, labels.nationalTeam, tv(player.seleccionNacional, 'nationalTeam'));
    pushNonEmptyRow(profileRows, labels.team, player.equipo);
    pushNonEmptyRow(profileRows, labels.secondaryLeague, player.liga2);
    if (player.groupes && player.groupes.length > 0) {
      const groupesLabel = player.groupes.map((g) => g.libelle).join(', ');
      pushNonEmptyRow(profileRows, labels.groups, groupesLabel);
    }

    if (profileRows.length > 0) {
      addNewPageIfNeeded(40);
      doc.autoTable({
        startY: yPos,
        head: [],
        body: profileRows.map((r) => [r[0], r[1]]),
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        margin: { left: marginLeft, right: marginRight, bottom: bottomMargin },
        tableWidth: 'auto'
      });
      yPos = doc.lastAutoTable.finalY + 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    }
  }

  if (includeCaracteristicas) {
    const caracRows = [];
    pushNonEmptyRow(caracRows, labels.position, tv(player.poste, 'position'));
    pushNonEmptyRow(caracRows, labels.dominantHand, tv(player.mano, 'hand'));
    pushNonEmptyRow(caracRows, labels.shootingRange, player.shootingRange);
    pushNonEmptyRow(caracRows, labels.offensiveRole, tv(player.rolOfensivo, 'offensiveRole'));
    pushNonEmptyRow(caracRows, labels.defensiveRole, tv(player.rolDefensivo, 'defensiveRole'));
    pushNonEmptyRow(caracRows, labels.jeJiSkills, player.jeJiType);
    if (player.jeJiAttributes && player.jeJiAttributes.length > 0) {
      pushNonEmptyRow(
        caracRows,
        `${labels.attributes} (${player.jeJiType || ''})`,
        player.jeJiAttributes.join(', ')
      );
    }
    pushNonEmptyRow(caracRows, labels.flagBehaviour, tv(player.comportamiento, 'flagBehaviour'));
    pushNonEmptyRow(caracRows, labels.injuryProne, tv(player.injury, 'flagBehaviour'));
    pushNonEmptyRow(caracRows, labels.currentLevel, tv(player.nivel, 'level'));
    pushNonEmptyRow(caracRows, labels.projection, tv(player.proyeccion, 'level'));
    pushNonEmptyRow(caracRows, labels.proComparison, player.comparacionPro);

    if (caracRows.length > 0) {
      addNewPageIfNeeded(40);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(sections.characteristics, marginLeft, yPos);
      yPos += 6;
      doc.autoTable({
        startY: yPos,
        head: [],
        body: caracRows.map((r) => [r[0], r[1]]),
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        margin: { left: marginLeft, right: marginRight, bottom: bottomMargin },
        tableWidth: 'auto'
      });
      yPos = doc.lastAutoTable.finalY + 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    }
  }

  if (includeExperiencia) {
    const expRows = [];
    expRows.push([labels.nbaExperience, tv(player.nbaExperience, 'yesNo') || '']);
    expRows.push([labels.acbExperience, tv(player.experienciaAcb, 'yesNo') || '']);
    expRows.push([labels.europeExperience, tv(player.experiencaEuropa, 'yesNo') || '']);
    expRows.push([labels.ncaaExperience, tv(player.experienca_ncaa, 'yesNo') || '']);
    pushNonEmptyRow(expRows, labels.draftYear, player.añoDraft);
    pushNonEmptyRow(expRows, labels.draftPosition, player.posicionDraft);
    pushNonEmptyRow(expRows, labels.preDraftTeam, player.equipoPreDraft);
    pushNonEmptyRow(expRows, labels.pit, player.pit);
    pushNonEmptyRow(expRows, labels.eurocamp, player.eurocamp);

    if (expRows.length > 0) {
      addNewPageIfNeeded(30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(sections.professionalExperience, marginLeft, yPos);
      yPos += 6;
      doc.autoTable({
        startY: yPos,
        head: [],
        body: expRows.map((r) => [r[0], r[1]]),
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        margin: { left: marginLeft, right: marginRight, bottom: bottomMargin },
        tableWidth: 'auto'
      });
      yPos = doc.lastAutoTable.finalY + 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    }
  }

  if (includeContrato) {
    const contractRows = [];
    pushNonEmptyRow(contractRows, labels.agency, player.agencia);
    pushNonEmptyRow(contractRows, labels.salaryRange, player.rangoPrecios);
    pushNonEmptyRow(contractRows, labels.contract, player.contrato);
    pushNonEmptyRow(contractRows, labels.buyout, player.buyout);

    if (contractRows.length > 0) {
      addNewPageIfNeeded(20);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(sections.contractInfo, marginLeft, yPos);
      yPos += 6;
      doc.autoTable({
        startY: yPos,
        head: [],
        body: contractRows.map((r) => [r[0], r[1]]),
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        margin: { left: marginLeft, right: marginRight, bottom: bottomMargin },
        tableWidth: 'auto'
      });
      yPos = doc.lastAutoTable.finalY + 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    }
  }

  // Fonction locale pour dessiner du texte (Intel/Informe)
  const drawTextBlockLocal = (text) => {
    const desiredFontFamily = 'helvetica';
    const desiredFontStyle = 'normal';
    const desiredFontSize = 11;
    doc.setFont(desiredFontFamily, desiredFontStyle);
    doc.setFontSize(desiredFontSize);
    yPos = drawTextBlock(
      doc,
      text,
      yPos,
      marginLeft,
      contentWidth,
      pageHeight,
      bottomMargin,
      desiredFontFamily,
      desiredFontStyle,
      desiredFontSize
    );
  };

  // 5) INFORMES sélectionnés
  if (checkedInformes && checkedInformes.length > 0) {
    addNewPageIfNeeded(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(sections.report, marginLeft, yPos);
    yPos += 12;

    checkedInformes.forEach((inf) => {
      const scoutingType = formatScoutingType(inf.visu);
      const detalles = convertHtmlToText(inf.detalles);
      const headerLines =
        (scoutingType ? 1 : 0) +
        (inf.evento ? 1 : 0) +
        (inf.equipo ? 1 : 0);
      const blockHeight = measureBlockHeight(6, headerLines, detalles);
      ensureBlockFits(blockHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(`${new Date(inf.fecha).toLocaleDateString()} (${inf.usuario_nombre})`, marginLeft, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);

      if (scoutingType) {
        doc.text(`${labels.scouting}: ${scoutingType}`, marginLeft, yPos);
        yPos += 6;
      }
      if (inf.evento) {
        doc.text(`${labels.event}: ${inf.evento}`, marginLeft, yPos);
        yPos += 6;
      }
      if (inf.equipo) {
        doc.text(`${labels.team}: ${inf.equipo}`, marginLeft, yPos);
        yPos += 6;
      }

      if (detalles) {
        drawTextBlockLocal(detalles);
        yPos += 4;
      } else {
        yPos += 4;
      }
    });
  }

  // 6) INTELS sélectionnés
  if (checkedIntels && checkedIntels.length > 0) {
    addNewPageIfNeeded(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(sections.intel, marginLeft, yPos);
    yPos += 12;

    checkedIntels.forEach((intel) => {
      const detallesIntel = convertHtmlToText(intel.detalles);
      const headerLines = intel.fuente ? 1 : 0;
      const blockHeight = measureBlockHeight(6, headerLines, detallesIntel);
      ensureBlockFits(blockHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`${new Date(intel.fecha).toLocaleDateString()} (${intel.usuario_nombre})`, marginLeft, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);

      if (intel.fuente) {
        doc.text(`${labels.source}: ${intel.fuente}`, marginLeft, yPos);
        yPos += 6;
      }
      if (detallesIntel) {
        drawTextBlockLocal(detallesIntel);
        yPos += 4;
      } else {
        yPos += 4;
      }
    });
  }

  // 7) Nom du fichier PDF
  const today = new Date();
  const formattedDate = today.toLocaleDateString().replace(/\//g, '-');
  // Utiliser le nom du club
  const clubName = clubInfo.name || 'Club';
  const filenameParts = [`${clubName} ${filenamePrefix}`];
  if (player.nom && player.nom.trim() !== '') {
    filenameParts.push(player.nom.trim());
  }
  if (player.equipo && player.equipo.trim() !== '') {
    filenameParts.push(player.equipo.trim());
  }
  filenameParts.push(formattedDate);

  const fileName = filenameParts.join(' - ') + '.pdf';
  doc.save(fileName);
}
